/**
 * Civil calendar dates (YYYY-MM-DD) + bar IANA timezone — Luxon-based.
 * Do not use new Date("YYYY-MM-DD") for venue schedule display; it parses as UTC midnight.
 * Shared by client (webpack) and Node server/tests.
 */

const { DateTime, IANAZone } = require("luxon");

const DEFAULT_BAR_TIME_ZONE = "America/New_York";

const YMD_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * @param {string} [zone]
 * @returns {boolean}
 */
function isValidIanaTimeZone(zone) {
  if (zone == null || typeof zone !== "string" || !zone.trim()) return false;
  return IANAZone.isValidZone(zone.trim());
}

/**
 * @param {string|null|undefined} zone
 * @returns {string}
 */
function normalizeBarTimeZone(zone) {
  const z = typeof zone === "string" ? zone.trim() : "";
  return isValidIanaTimeZone(z) ? z : DEFAULT_BAR_TIME_ZONE;
}

/**
 * @param {string|null|undefined} value
 * @returns {string} YYYY-MM-DD or ""
 */
function parseCivilDateOnly(value) {
  if (value == null || value === "") return "";
  if (typeof value === "string") {
    const t = value.trim();
    if (YMD_RE.test(t)) return t;
    if (/^\d{4}-\d{2}-\d{2}T/.test(t)) return t.slice(0, 10);
    if (/^\d{4}-\d{2}-\d{2}/.test(t)) return t.slice(0, 10);
    return "";
  }
  return "";
}

/**
 * Calendar "today" as YYYY-MM-DD in the given IANA zone (venue-local intent).
 * @param {string} [timeZone]
 * @param {Date} [now] - for testing
 * @returns {string}
 */
function civilDateTodayInZone(timeZone, now = new Date()) {
  const zone = normalizeBarTimeZone(timeZone);
  return DateTime.fromJSDate(now).setZone(zone).toISODate();
}

/**
 * Long label: weekday + month + day + year in venue zone (civil date is interpreted in that zone).
 * @param {string} civilYmd
 * @param {string} [timeZone]
 * @param {string} [locale]
 */
function formatCivilDateLong(civilYmd, timeZone, locale = "en-US") {
  const ymd = parseCivilDateOnly(civilYmd);
  if (!ymd) return "";
  const zone = normalizeBarTimeZone(timeZone);
  const dt = DateTime.fromISO(ymd, { zone });
  if (!dt.isValid) return civilYmd;
  return dt.setLocale(locale).toLocaleString({
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

/**
 * Compact label (e.g. weekday short) for lists.
 */
function formatCivilDateMedium(civilYmd, timeZone, locale = "en-US") {
  const ymd = parseCivilDateOnly(civilYmd);
  if (!ymd) return "";
  const zone = normalizeBarTimeZone(timeZone);
  const dt = DateTime.fromISO(ymd, { zone });
  if (!dt.isValid) return civilYmd;
  return dt.setLocale(locale).toLocaleString({
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/**
 * Notification-style minimal date: M/D/YY in venue zone (matches prior product tone).
 */
function formatCivilDateNotificationShort(civilYmd, timeZone) {
  const ymd = parseCivilDateOnly(civilYmd);
  if (!ymd) return "";
  const zone = normalizeBarTimeZone(timeZone);
  const dt = DateTime.fromISO(ymd, { zone });
  if (!dt.isValid) return civilYmd;
  const month = dt.month;
  const day = dt.day;
  const yy = String(dt.year).slice(-2);
  return `${month}/${day}/${yy}`;
}

/**
 * Lexicographic compare for YYYY-MM-DD strings.
 * @returns {number} -1 | 0 | 1
 */
function compareCivilYmd(a, b) {
  const sa = parseCivilDateOnly(a);
  const sb = parseCivilDateOnly(b);
  if (sa < sb) return -1;
  if (sa > sb) return 1;
  return 0;
}

/**
 * Parse HH:MM or HH:MM:SS (MySQL TIME) to hour, minute, second numbers.
 */
function parseTimeParts(timeStr) {
  if (timeStr == null || timeStr === "") return null;
  const s = String(timeStr);
  const m = s.match(/(\d{1,2}):(\d{2})(?::(\d{2}))?/);
  if (!m) return null;
  const h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  const sec = m[3] != null ? parseInt(m[3], 10) : 0;
  if (Number.isNaN(h) || Number.isNaN(min) || Number.isNaN(sec)) return null;
  return { hour: h, minute: min, second: sec };
}

/**
 * Absolute instant for civil date + wall time in venue zone.
 * @param {string} civilYmd
 * @param {string} timeStr - TIME string
 * @param {string} [timeZone]
 * @returns {Date|null}
 */
function civilDateTimeToJsDate(civilYmd, timeStr, timeZone) {
  const ymd = parseCivilDateOnly(civilYmd);
  const parts = parseTimeParts(timeStr);
  if (!ymd || !parts) return null;
  const zone = normalizeBarTimeZone(timeZone);
  const [y, mo, d] = ymd.split("-").map((x) => parseInt(x, 10));
  let dt = DateTime.fromObject(
    {
      year: y,
      month: mo,
      day: d,
      hour: parts.hour,
      minute: parts.minute,
      second: parts.second,
    },
    { zone },
  );
  if (!dt.isValid) return null;
  return dt.toJSDate();
}

/**
 * End datetime for a show, handling overnight (end local time before start same civil day).
 * @param {string} civilYmd
 * @param {string} startTimeStr
 * @param {string} endTimeStr
 * @param {string} [timeZone]
 * @returns {Date|null}
 */
function combineCivilDateAndLocalTimes(
  civilYmd,
  startTimeStr,
  endTimeStr,
  timeZone,
) {
  const start = civilDateTimeToJsDate(civilYmd, startTimeStr, timeZone);
  const end = civilDateTimeToJsDate(civilYmd, endTimeStr, timeZone);
  if (!start || !end) return null;
  if (end <= start) {
    const ymd = parseCivilDateOnly(civilYmd);
    const zone = normalizeBarTimeZone(timeZone);
    const nextDay = DateTime.fromISO(ymd, { zone }).plus({ days: 1 });
    const parts = parseTimeParts(endTimeStr);
    if (!parts || !nextDay.isValid) return end;
    const dt = DateTime.fromObject(
      {
        year: nextDay.year,
        month: nextDay.month,
        day: nextDay.day,
        hour: parts.hour,
        minute: parts.minute,
        second: parts.second,
      },
      { zone },
    );
    return dt.isValid ? dt.toJSDate() : end;
  }
  return end;
}

/**
 * Start-of-day and end-of-day instants in zone for range filtering (inclusive civil dates).
 * Returns UTC Dates suitable for comparing with show.date string ranges via string compare instead —
 * prefer civil string range for DATE columns; this is for Date-based filters only.
 */
function civilYmdStartOfDayUtc(civilYmd, timeZone) {
  const ymd = parseCivilDateOnly(civilYmd);
  if (!ymd) return null;
  const zone = normalizeBarTimeZone(timeZone);
  const dt = DateTime.fromISO(ymd, { zone }).startOf("day");
  return dt.isValid ? dt.toUTC().toJSDate() : null;
}

module.exports = {
  DEFAULT_BAR_TIME_ZONE,
  isValidIanaTimeZone,
  normalizeBarTimeZone,
  parseCivilDateOnly,
  civilDateTodayInZone,
  formatCivilDateLong,
  formatCivilDateMedium,
  formatCivilDateNotificationShort,
  compareCivilYmd,
  parseTimeParts,
  civilDateTimeToJsDate,
  combineCivilDateAndLocalTimes,
  civilYmdStartOfDayUtc,
};
